export type VehicleClass = 'car' | 'midsize' | 'large' | 'oversized';

export const VEHICLE_CLASSES: { id: VehicleClass; label: string; example: string }[] = [
  { id: 'car', label: 'Coupe / Sedan', example: 'Civic, Camry, Mustang' },
  { id: 'midsize', label: 'Mid-size SUV', example: 'CR-V, RAV4, Equinox' },
  { id: 'large', label: 'Truck / Full SUV', example: 'F-150, Tahoe, Silverado' },
  { id: 'oversized', label: 'Van / XL / 3-Row', example: 'Suburban, Sprinter, Odyssey' },
];

export interface Package {
  id: string;
  name: string;
  description: string;
  category: 'interior' | 'exterior' | 'full';
  features: string[];
  prices: Record<VehicleClass, number>;
  durationHours: number;
  popular: boolean;
  active: boolean;
  order: number;
}

export interface Addon {
  id: string;
  name: string;
  description: string;
  price: number;
  fromPrice: boolean;
  active: boolean;
  order: number;
}

export interface CustomService {
  id: string;
  name: string;
  description: string;
  icon: string; // lucide icon name key, see ICON_MAP
  features: string[];
  priceFrom: number | null;
  comingSoon: boolean;
  active: boolean;
  order: number;
}

// ── Accounts ──────────────────────────────────────────────────
export type StaffRole = 'admin' | 'cleaner';

export interface StaffMember {
  id: string; // auth uid
  role: StaffRole;
  email: string;
  name: string;
  addedAt: number;
}

export interface UserVehicle {
  id: string;
  year: string;
  make: string;
  model: string;
  vehicleClass: VehicleClass;
  color: string;
}

export interface UserProfile {
  name: string;
  email: string;
  phone: string;
  vehicles: UserVehicle[];
  createdAt: number;
}

export type BookingStatus = 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';

export const BOOKING_STATUSES: { id: BookingStatus; label: string }[] = [
  { id: 'pending', label: 'Pending' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
];

export interface Booking {
  id: string;
  createdAt: number;
  status: BookingStatus;
  uid: string; // auth uid when booked by a signed-in customer, '' for guests
  customer: { name: string; email: string; phone: string };
  vehicle: { year: string; make: string; model: string; vehicleClass: VehicleClass };
  packageId: string;
  packageName: string;
  addons: { id: string; name: string; price: number }[];
  date: string; // YYYY-MM-DD
  timeSlot: string; // e.g. "9:00 AM"
  serviceLocation: 'shop' | 'mobile';
  address: string;
  postalCode: string;
  travelFee: number;
  zoneName: string;
  outsideArea: boolean;
  promoCode: string;
  discount: number;
  estimatedTotal: number;
  notes: string;
  adminNotes: string;
}

export type QuoteStatus = 'new' | 'contacted' | 'quoted' | 'won' | 'lost';

export interface QuoteRequest {
  id: string;
  createdAt: number;
  status: QuoteStatus;
  serviceName: string;
  customer: { name: string; email: string; phone: string };
  vehicle: string;
  details: string;
  budget: string;
  adminNotes: string;
}

export interface Testimonial {
  id: string;
  createdAt: number;
  name: string;
  vehicle: string;
  rating: number;
  text: string;
  approved: boolean;
  featured: boolean;
}

export interface GalleryItem {
  id: string;
  createdAt: number;
  url: string;
  storagePath: string; // empty for external/placeholder URLs
  caption: string;
  category: string;
  beforeUrl?: string; // when set, the site renders a before/after slider
}

export interface PromoCode {
  id: string; // the code itself, uppercase (doc id)
  type: 'percent' | 'fixed';
  value: number;
  active: boolean;
  expires: string; // YYYY-MM-DD, '' = never
  note: string;
}

export const GALLERY_CATEGORIES = ['exterior', 'interior', 'paint correction', 'ceramic coating', 'audio', 'lighting', 'wraps'] as const;

export interface DayHours {
  open: string; // "08:00"
  close: string; // "18:00"
  closed: boolean;
}

// ── Mobile service / travel fees ──────────────────────────────
// 'off'     — shop drop-off only
// 'flat'    — mobile service anywhere in Canada for one flat fee
// 'regions' — only postal codes within a radius of each region's
//             center are served; everything else is blocked
export type TravelMode = 'off' | 'flat' | 'regions';

export interface TravelRegion {
  id: string;
  name: string; // e.g. "Ottawa"
  centerPostal: string; // center postal code / FSA, e.g. "K1P"
  radiusKm: number;
  fee: number; // travel fee in CAD (0 = free)
}

export interface TravelSettings {
  mode: TravelMode;
  flatFee: number;
  regions: TravelRegion[];
  allowOutsideRequests: boolean;
  outsideMessage: string;
}

export interface SiteSettings {
  businessName: string;
  tagline: string;
  heroTitle: string;
  heroSubtitle: string;
  heroImage: string; // URL of the big homepage image
  phone: string;
  email: string;
  city: string;
  serviceArea: string;
  aboutText: string;
  travel: TravelSettings;
  allowDropoff: boolean;
  shopAddress: string;
  announcementEnabled: boolean;
  announcementText: string;
  socials: { instagram: string; facebook: string; tiktok: string; youtube: string; google: string };
  hours: Record<string, DayHours>; // keys: mon..sun
  booking: {
    slotStart: number; // first slot hour, 24h
    slotEnd: number; // last slot hour, 24h
    slotIntervalHours: number;
    daysAhead: number;
    minLeadHours: number;
  };
  notifications: {
    vapidKey: string; // Web Push certificate key from Firebase console
  };
}

export interface Faq {
  id: string;
  question: string;
  answer: string;
  order: number;
}

export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export const DAY_LABELS: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};
